import cx from 'classnames';
import {find, isFunction, map} from 'lodash';
import {useState, useEffect, Fragment, useMemo, useCallback} from 'react';
import {useTranslation} from 'react-i18next';

import {groupApi} from 'api';
import {Select} from 'components';
import {Option, Group, Subgroup} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {FieldValidationError} from 'types';
import {apiUtils} from 'utils';

type Props = {
    children?: (options: {renderGroup, renderSubgroup}) => React.ReactElement;
    emptyGroupOption?: 'none' | 'all' | null;
    collapseEmptyOption?: boolean;
    fullWidth?: boolean;
    groups?: Group[];
    selectedGroupId?: string;
    selectedSubgroupId?: string;
    disabled?: boolean;
    groupDisabled?: boolean;
    groupError?: FieldValidationError;
    subgroupError?: FieldValidationError;
    grid?: boolean;
    onChange: (groupId: string, subgroupId: string) => void;
};

export type AssignmentSelectorsProps = Props;

const T_KEYS = TRANSLATION_KEYS.COMPONENTS.ASSIGNMENT_SELECTORS;

export const AssignmentSelectors: React.FC<Props> = ({
    children, fullWidth = true, selectedGroupId, selectedSubgroupId, groups: groupsProp, groupDisabled,
    groupError, subgroupError, onChange, emptyGroupOption = 'none', collapseEmptyOption = true, disabled, grid
}: Props) => {
    const {t} = useTranslation();
    const [groups, setGroups] = useState<Group[]>([]);
    const [selectedGroup, setSelectedGroup] = useState<Group>(null);
    const [selectedSubgroup, setSelectedSubgroup] = useState<Subgroup>(null);

    useEffect(() => {
        if (groupsProp) {
            setGroups(groupsProp);
        } else {
            groupApi.getGroups()
                .then((fetchedGroups: Group[]) => {
                    setGroups(fetchedGroups);
                });
        }
    }, [groupsProp]);

    useEffect(() => {
        setSelectedGroup(find(groups, {id: selectedGroupId}));
    }, [groups, selectedGroupId]);

    useEffect(() => {
        if (selectedGroup) {
            setSelectedSubgroup(find(selectedGroup.subgroups, {id: selectedSubgroupId}));
        } else {
            setSelectedSubgroup(null);
        }
    }, [selectedGroup, selectedSubgroupId]);

    const groupErrorMessage = useMemo(() => apiUtils.getErrorMessageByCode(t, groupError), [t, groupError]);
    const subgroupErrorMessage = useMemo(() => apiUtils.getErrorMessageByCode(t, subgroupError), [t, subgroupError]);

    const handleGroupChange = useCallback(({id}: Option) => {
        onChange(id, null);
    }, [onChange]);

    const handleSubgroupChange = useCallback(({id}: Option) => {
        onChange(selectedGroup.id, id);
    }, [selectedGroup, onChange]);

    const emptyGroupOptionLabel = useMemo<string | null>(() => {
        switch (emptyGroupOption) {
            case 'all':
                return t(T_KEYS.ALL_GROUPS);
            case 'none':
                return t(T_KEYS.NONE);
            default:
                return null;
        }
    }, [emptyGroupOption, t]);

    const groupOptions: Option[] = useMemo(() => {
        const result = map(groups, ({id, name}) => ({
            id,
            name
        }));
        if (emptyGroupOptionLabel) {
            result.unshift({
                id: '',
                name: emptyGroupOptionLabel
            });
        }
        return result;
    }, [groups, emptyGroupOptionLabel]);

    const groupValue = useMemo(() => groupOptions.find((option) => {
        if (!selectedGroup?.id) {
            return !option.id;
        }
        return option.id === selectedGroup?.id;
    }), [groupOptions, selectedGroup?.id]);

    const subgroupOptions = useMemo(() => {
        const result = map(selectedGroup?.subgroups, (subgroup) => ({
            id: subgroup.id,
            name: subgroup.name
        }));

        result.unshift({
            id: '',
            name: t(T_KEYS.NONE)
        });

        return result;

    }, [t, selectedGroup?.subgroups]);

    const subgroupValue = useMemo(() => find(subgroupOptions, (option) => {
        if (!selectedSubgroup?.id) {
            return !option.id;
        }
        return option.id === selectedSubgroup?.id;
    }), [subgroupOptions, selectedSubgroup?.id]);

    const renderGroup = () => (
        <Select
            disabled={disabled || groupDisabled}
            className={cx({'tw-w-full': fullWidth})}
            placeholder={t(T_KEYS.GROUP)}
            options={groupOptions}
            value={groupValue}
            errorMessage={groupErrorMessage}
            collapseEmptyOption={collapseEmptyOption}
            onChange={handleGroupChange}
        />
    );

    const renderSubgroup = () => (
        <Select
            disabled={!selectedGroup || disabled}
            className={cx({'tw-w-full': fullWidth})}
            placeholder={t(T_KEYS.SUBGROUP)}
            options={subgroupOptions}
            value={subgroupValue}
            errorMessage={subgroupErrorMessage}
            collapseEmptyOption={collapseEmptyOption}
            onChange={handleSubgroupChange}
        />
    );

    return (
        isFunction(children) ?
            children({renderGroup, renderSubgroup}) :
            grid ?
                <div className='tw-w-full tw-grid tw-grid-cols-1 md:tw-grid-cols-2 md:tw-gap-6 tw-gap-1'>
                    {renderGroup()}
                    {renderSubgroup()}
                </div> :
                <Fragment>
                    {renderGroup()}
                    {renderSubgroup()}
                </Fragment>
    );
};
